// DCF 프로젝트 스토어 — 바닐라 dcf.js 의 ST/loadProjects/saveProjects/
// serverListProjects/serverSaveProject/refreshProjectsFromServer/createProject/
// openProject/saveProject/recalc 를 zustand 로 이식.
//
// ⚠ 호환 계약 (바닐라 ↔ React 양방향):
//  - localStorage 키: `va_dcf_projects::<userKey>` (userKey = uid||username||email 소문자)
//  - 프로젝트 스냅샷: {id,name,date,payload,result?,pps,updatedAt}
//  - 진실 소스는 서버 DB(/agent/dcf-projects), localStorage 는 즉시 렌더용 캐시.
import { create } from "zustand";
import { api, AuthError } from "../lib/api";
import { todayStr } from "../lib/format";
import type {
  DcfForecastDrivers,
  DcfPayload,
  DcfProject,
  DcfResult,
  DcfVersion,
  ScenarioArray,
  WaccComputeBody,
} from "../types/dcf";
import { useAuth, type User } from "./auth";

const PROJECTS_BASE = "va_dcf_projects";

/** 바닐라 userScopeKey 와 동일 규칙 — uid||username||email 소문자, 없으면 va_active_user */
export function userScopeKey(user: User | null | undefined): string | null {
  const fromUser = user
    ? user.uid || user.username || (user["email"] as string | undefined)
    : null;
  let k = String(fromUser || "").trim().toLowerCase();
  if (!k) {
    try {
      k = (localStorage.getItem("va_active_user") || "").trim().toLowerCase();
    } catch {
      /* noop */
    }
  }
  return k || null;
}

function scopedKey(userKey: string | null): string {
  return `${PROJECTS_BASE}::${userKey || "_anon"}`;
}

function loadLocal(userKey: string | null): DcfProject[] {
  try {
    return JSON.parse(
      localStorage.getItem(scopedKey(userKey)) || "[]",
    ) as DcfProject[];
  } catch {
    return [];
  }
}

function saveLocal(userKey: string | null, arr: DcfProject[]) {
  try {
    localStorage.setItem(scopedKey(userKey), JSON.stringify(arr.slice(0, 100)));
  } catch {
    /* quota 초과 등 — 캐시 저장 실패는 무시(서버가 진실 소스) */
  }
}

// ── 서버 DB 연동 (바닐라와 동일 의미: 실패 시 null/false, 절대 throw 로 흐름 끊지 않음) ──
interface OkResp {
  ok: boolean;
  message?: string;
}

async function serverListProjects(): Promise<DcfProject[] | null> {
  try {
    const d = await api<OkResp & { items?: DcfProject[] }>("/agent/dcf-projects");
    return d.ok ? d.items || [] : null;
  } catch {
    return null;
  }
}

async function serverSaveProject(proj: DcfProject): Promise<boolean> {
  try {
    const d = await api<OkResp>("/agent/dcf-projects", {
      method: "POST",
      body: JSON.stringify({ project: proj }),
    });
    if (!d.ok) console.warn("[dcf-projects] save 실패:", d.message);
    return !!d.ok;
  } catch (e) {
    console.warn("[dcf-projects] save 예외:", e);
    return false;
  }
}

async function serverDeleteProject(id: string): Promise<void> {
  try {
    await api(`/agent/dcf-projects/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  } catch {
    /* best-effort */
  }
}

// ── 프로젝트 버전(스냅샷) 서버 연동 — 바닐라 serverListVersions/serverSaveVersion/serverDeleteVersion ──
async function serverListVersions(pid: string): Promise<DcfVersion[] | null> {
  try {
    const d = await api<OkResp & { items?: DcfVersion[] }>(
      `/agent/dcf-projects/${encodeURIComponent(pid)}/versions`,
    );
    return d.ok ? d.items || [] : null;
  } catch {
    return null;
  }
}

async function serverSaveVersion(pid: string, ver: DcfVersion): Promise<boolean> {
  try {
    const d = await api<OkResp>(
      `/agent/dcf-projects/${encodeURIComponent(pid)}/versions`,
      { method: "POST", body: JSON.stringify({ version: ver }) },
    );
    return !!d.ok;
  } catch {
    return false;
  }
}

async function serverDeleteVersion(pid: string, vid: string): Promise<void> {
  try {
    await api(
      `/agent/dcf-projects/${encodeURIComponent(pid)}/versions/${encodeURIComponent(vid)}`,
      { method: "DELETE" },
    );
  } catch {
    /* best-effort */
  }
}

/** 바닐라 saveWithVersion 의 자동 라벨 — "MM/DD HH:MM" 근사 표기 */
function autoVersionLabel(now: Date): string {
  return now
    .toLocaleString("ko-KR", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(". ", "/")
    .replace(". ", "")
    .replace(". ", "");
}

// ── 시나리오 드라이버 배열 헬퍼 (바닐라 scenArr/setScenArr) ──
export function scenArr(
  fd: DcfForecastDrivers,
  key: string,
  scen: string,
): number[] {
  const v = fd[key] as ScenarioArray | null | undefined;
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return v[scen] || v["business_plan"] || v["historical"] || v["user_input"] || [];
}

/** 기존 객체의 다른 시나리오 값을 보존하며 scen 키만 교체한 새 ScenarioArray 반환 */
export function withScenArr(
  prev: ScenarioArray | null | undefined,
  scen: string,
  arr: number[],
): ScenarioArray {
  const base: { [k: string]: number[] | undefined } =
    prev && !Array.isArray(prev) ? { ...prev } : {};
  base[scen] = arr;
  return base;
}

/** 바닐라 fillForm 의 useWaccModule 판정 — WACC 모듈(peer 산출) 사용 프로젝트 여부 */
export function usesWaccModule(p: DcfPayload): boolean {
  return !!(
    (p.peer_symbols && p.peer_symbols.length) ||
    p.peer_input_method === "auto" ||
    p.wacc_input
  );
}

/** 바닐라 validateRequired — 백엔드 422 전에 필수값을 안내 */
export function validateRequired(p: DcfPayload): string[] {
  const out: string[] = [];
  const no = p.non_operating || {};
  if (!(Number(no.shares_outstanding) > 0))
    out.push("발행주식수 (Assumptions)");
  if (!(Number(p.discount_rate) > 0) && !usesWaccModule(p))
    out.push("WACC (할인율)");
  if (usesWaccModule(p)) {
    if (p.peer_input_method === "direct" && !(p.peer_symbols && p.peer_symbols.length))
      out.push("WACC Peer 종목 (WACC 탭)");
    if (p.peer_input_method === "auto" && (p.business_text || "").length < 10)
      out.push("WACC 자동 Peer용 사업내용 (WACC 탭)");
  }
  if (!(Number(p.terminal_growth_rate) >= 0)) out.push("영구성장률 g");
  if (!(Number(p.tax_rate) >= 0)) out.push("법인세율");
  return out;
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** 깊은 머지 (바닐라 deepMerge) — nested 객체는 재귀 머지, 배열/원시값은 통째로 교체 */
export function deepMerge(target: unknown, patch: unknown): unknown {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) return patch;
  const base: Record<string, unknown> =
    target && typeof target === "object" && !Array.isArray(target)
      ? { ...(target as Record<string, unknown>) }
      : {};
  for (const [k, pv] of Object.entries(patch as Record<string, unknown>)) {
    if (pv && typeof pv === "object" && !Array.isArray(pv)) {
      base[k] = deepMerge(base[k], pv);
    } else {
      base[k] = pv; // primitive / 배열 / null → 통째로 교체
    }
  }
  return base;
}

/** 에이전트 patch 객체를 leaf 경로 배열로 평탄화 — 배열/원시값(null 포함)이 leaf.
 *  예: {non_operating:{interest_bearing_debt:100}} → ["non_operating.interest_bearing_debt"] */
export function flattenPatchPaths(
  patch: Record<string, unknown>,
  prefix = "",
): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(patch)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const sub = flattenPatchPaths(v as Record<string, unknown>, path);
      if (sub.length) out.push(...sub);
      else out.push(path); // 빈 객체 patch 도 그 경로가 대상임은 기록
    } else {
      out.push(path);
    }
  }
  return out;
}

/** paths 에 prefix 와 같거나 그 하위(prefix.xxx)인 경로가 있는지 */
export function pathsInclude(paths: string[], prefix: string): boolean {
  return paths.some((p) => p === prefix || p.startsWith(prefix + "."));
}

/** 바닐라 applyDcfPatch 의 changedTabs 판정 — patch 가 건드린 탭 id 목록 */
export function patchTabsOf(paths: string[]): string[] {
  const tabs: string[] = [];
  const assumpKeys = [
    "discount_rate",
    "terminal_growth_rate",
    "tax_rate",
    "forecast_full_years",
    "non_operating",
  ];
  if (assumpKeys.some((k) => pathsInclude(paths, k))) tabs.push("assumptions");
  if (pathsInclude(paths, "forecast_drivers")) tabs.push("projection");
  return tabs;
}

/** Report 탭 파일 목록 항목 — 채팅 download artifact 와 산출물 생성이 공유 */
export interface DcfFileLink {
  id: number;
  kind: "ok" | "warn" | "err";
  label: string;
  url?: string;
}

/** 401 → 인증 재검증(user null 로 떨어지며 로그인 오버레이 표시). true = 인증 오류였음 */
function handleAuthError(e: unknown): boolean {
  if (e instanceof AuthError) {
    void useAuth.getState().checkAuth();
    return true;
  }
  return false;
}

export interface DcfCurrent {
  project: DcfProject;
  payload: DcfPayload;
  result: DcfResult | null;
}

interface DcfState {
  userKey: string | null;
  projects: DcfProject[];
  current: DcfCurrent | null;
  /** 로딩 오버레이 메시지 (null = 대기 없음) */
  busy: string | null;
  /** 현재 프로젝트의 버전 스냅샷 목록 (바닐라 _versions) */
  versions: DcfVersion[];
  /** Report 탭 파일 목록 (바닐라 #file-links) — 채팅 download artifact 도 여기에 추가 */
  fileLinks: DcfFileLink[];
  /** 빈 양식으로 방금 만든 프로젝트 id — 채팅 인트로 문구 분기(바닐라 openProject autocalc===false) */
  blankIntroId: string | null;
  /** 마지막 에이전트 patch 가 건드린 payload 경로 — 입력 셀 cell-flash 용 (1.5초 후 자동 해제) */
  lastPatchPaths: string[];
  /** patch 적용 횟수 — 같은 값이 다시 patch 돼도 flash 를 재발화시키는 시퀀스 */
  patchSeq: number;
  /** patch 가 건드린 탭 id — 탭 버튼 파란 dot, 해당 탭 방문 시 해제 */
  patchDotTabs: string[];

  /** 로그인 사용자 기준 초기화 — 로컬 캐시 즉시 표시 후 서버와 병합 동기화 */
  init: (user: User | null) => void;
  /** 서버 우선 + 로컬전용 백필 병합 (바닐라 refreshProjectsFromServer) */
  refreshFromServer: () => Promise<void>;
  /** 샘플 페이로드로 프로젝트 생성·저장·열기. 성공 시 프로젝트 id */
  createFromSample: (name: string, date: string, fye: string) => Promise<string | null>;
  /** 빈 양식 프로젝트 생성 (바닐라 createProject 의 newMethod==='blank' 분기) —
   *  매출·CAPEX 는 0으로 비우고 비용비율은 샘플 기본값 유지, 자동 재계산 없이 연다 */
  createBlank: (name: string, date: string, fye: string) => Promise<string | null>;
  /** 파일 업로드 인테이크 (바닐라 createFromExcel) — intake→finalize→프로젝트 생성·열기 */
  createFromExcel: (
    files: File[],
    name: string,
    date: string,
    fye: string,
  ) => Promise<{ id: string; missing: string[] } | null>;
  openProject: (id: string) => boolean;
  closeProject: () => void;
  /** 현재 payload/result 를 프로젝트에 반영해 캐시+서버 저장 (pps/updatedAt 갱신 포함) */
  saveProject: () => void;
  deleteProject: (id: string) => void;
  /** 엔진 재계산 → result 갱신 → 저장. 성공 여부 반환 */
  recalc: () => Promise<boolean>;
  /** 경로 기반 payload 수정 (예: "non_operating.interest_bearing_debt") — undefined 는 삭제 */
  setPayloadField: (path: string, value: unknown) => void;
  /** WACC 직접입력 전환 — 모듈 키 제거 후 discount_rate 설정 (바닐라 syncWaccDiscountToPayload) */
  setDiscountRateDirect: (rate: number) => void;
  /** WACC 모듈 산출 적용 — discount_rate 제거 후 peer/wacc_input 키를 payload 에 기록
   *  (바닐라 syncWaccDiscountToPayload 의 useWaccModule=true 분기) */
  applyWaccModule: (body: WaccComputeBody) => void;
  /** 탭 컴포넌트에서 전역 로딩 오버레이를 쓰기 위한 헬퍼 (바닐라 showLoading/hideLoading) */
  setBusy: (msg: string | null) => void;

  /** 에이전트 dcf_patch artifact 를 payload 에 깊은 머지 후 저장 (바닐라 applyDcfPatch).
   *  변경 경로(lastPatchPaths)와 변경 탭(patchDotTabs)도 기록해 시각 피드백을 만든다. */
  applyPatch: (patch: Record<string, unknown>) => void;
  /** 탭 방문 시 해당 탭의 파란 dot 해제 */
  clearPatchDot: (tab: string) => void;
  /** Report 탭 파일 목록 교체 (바닐라 genFile 의 box.innerHTML='' 후 재구성) */
  setFileLinks: (links: DcfFileLink[]) => void;
  /** 파일 목록 맨 앞에 추가 (바닐라 채팅 download artifact 의 afterbegin 삽입) */
  addFileLink: (link: DcfFileLink) => void;

  /** 현재 프로젝트의 버전 목록 로드 (바닐라 loadVersions) */
  loadVersions: () => Promise<void>;
  /** 저장 + 자동 버전 스냅샷 기록 (바닐라 saveWithVersion) */
  saveWithVersion: () => Promise<void>;
  /** 버전 복원 → 저장 → 재계산 (바닐라 restoreVersion — confirm 은 호출측 책임) */
  restoreVersion: (vid: string) => Promise<void>;
  /** 버전 삭제 (바닐라 deleteVersion — confirm 은 호출측 책임) */
  deleteVersion: (vid: string) => void;
}

export const useDcf = create<DcfState>((set, get) => ({
  userKey: null,
  projects: [],
  current: null,
  busy: null,
  versions: [],
  fileLinks: [],
  blankIntroId: null,
  lastPatchPaths: [],
  patchSeq: 0,
  patchDotTabs: [],

  init: (user) => {
    const key = userScopeKey(user);
    if (key) {
      try {
        localStorage.setItem("va_active_user", key);
      } catch {
        /* noop */
      }
    }
    if (key !== get().userKey) {
      // 계정 전환 — 이전 계정의 워크스페이스 상태를 비운다 (바닐라 setAuthed 격리 로직)
      set({
        userKey: key,
        projects: loadLocal(key),
        current: null,
        versions: [],
        fileLinks: [],
      });
    } else {
      set({ projects: loadLocal(key) });
    }
    void get().refreshFromServer();
  },

  refreshFromServer: async () => {
    const scopeAtStart = get().userKey;
    const items = await serverListProjects();
    if (items === null) return; // 서버 오류 → 캐시 유지(절대 비우지 않음)
    if (scopeAtStart !== get().userKey) return; // 그 사이 계정 전환됨 → 무시
    const serverIds = new Set(items.map((p) => p && p.id).filter(Boolean));
    const local = loadLocal(scopeAtStart);
    // 서버에 아직 없는 로컬 프로젝트 → 서버로 업로드(백필)
    const localOnly = local.filter((p) => p && p.id && !serverIds.has(p.id));
    localOnly.forEach((p) => void serverSaveProject(p));
    // 병합: 서버 + 로컬전용 (id 기준 중복 제거, 서버 우선)
    const merged = items.concat(localOnly);
    saveLocal(scopeAtStart, merged);
    set({ projects: merged });
  },

  createFromSample: async (name, date, fye) => {
    set({ busy: "샘플 가정을 불러오는 중입니다..." });
    let payload: DcfPayload;
    try {
      const d = await api<OkResp & { payload?: DcfPayload }>("/agent/dcf/sample");
      if (!d.ok || !d.payload) throw new Error(d.message || "백엔드 오류");
      payload = d.payload;
    } catch (e) {
      set({ busy: null });
      if (!handleAuthError(e)) alert("샘플 페이로드 로드 실패: " + errMsg(e));
      return null;
    }
    payload.target_name = name;
    payload.valuation_date = date;
    payload.fiscal_year_end_month = parseInt(fye, 10) || 12;
    const proj: DcfProject = {
      id: "p" + Date.now(),
      name,
      date,
      payload,
      pps: null,
      updatedAt: todayStr(),
    };
    const arr = [proj, ...get().projects];
    saveLocal(get().userKey, arr);
    void serverSaveProject(proj);
    set({ projects: arr, busy: null });
    get().openProject(proj.id);
    return proj.id;
  },

  createBlank: async (name, date, fye) => {
    // 바닐라도 blank 를 샘플 payload 에서 출발시킨다 (비용비율 등 기본값 유지)
    set({ busy: "샘플 가정을 불러오는 중입니다..." });
    let payload: DcfPayload;
    try {
      const d = await api<OkResp & { payload?: DcfPayload }>("/agent/dcf/sample");
      if (!d.ok || !d.payload) throw new Error(d.message || "백엔드 오류");
      payload = d.payload;
    } catch (e) {
      set({ busy: null });
      if (!handleAuthError(e)) alert("샘플 페이로드 로드 실패: " + errMsg(e));
      return null;
    }
    payload.target_name = name;
    payload.valuation_date = date;
    payload.fiscal_year_end_month = parseInt(fye, 10) || 12;
    // 빈 양식 = 회사 고유 수치는 전부 비운다 — 샘플전자 숫자를 지우며 시작하지
    // 않게. 세율·영구성장률·물가 같은 일반 가정은 남긴다(보편 기본값이고, 지우면
    // 재계산 자체가 무의미해진다). 빈 양식은 autocalc=false 라 계산 강제도 없다.
    payload.business_description = "";
    payload.historical_pl = (payload.historical_pl || []).map((row) => {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(row))
        out[k] = typeof v === "number" ? 0 : v;   // 기간 라벨은 남기고 수치만 0
      return out as typeof row;
    });
    // historical_bs 를 빼먹으면 샘플전자의 운전자본이 그대로 남는다. 직전
    // 순운전자본은 이 마지막 연도에서 나오고(forecast.py prev_nwc) 첫해 FCF 에
    // 바로 꽂히므로, 남아 있으면 남의 회사 숫자로 평가액이 움직인다(리뷰 지적).
    payload.historical_bs = (payload.historical_bs || []).map((row) => {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(row))
        out[k] = typeof v === "number" ? 0 : v;   // 기간 라벨은 남기고 수치만 0
      return out as typeof row;
    });
    payload.revenue_segments = [];
    payload.fixed_assets = [];
    payload.capex_items = [];
    delete payload.discount_rate;      // WACC 는 직접 입력하거나 WACC 탭에서 산출
    delete payload.peer_symbols;
    delete payload.wacc_input;
    payload.non_operating = {
      non_operating_assets: 0,
      interest_bearing_debt: 0,
      preferred_stock_amount: 0,
      shares_outstanding: 0,
    };
    const fd: DcfForecastDrivers = (payload.forecast_drivers = {});
    fd.scenario_choice = "business_plan";
    fd.revenue_plan_amounts = [0, 0, 0, 0, 0];
    fd.capex_drivers = { business_plan: [0, 0, 0, 0, 0] };
    const proj: DcfProject = {
      id: "p" + Date.now(),
      name,
      date,
      payload,
      pps: null,
      updatedAt: todayStr(),
    };
    const arr = [proj, ...get().projects];
    saveLocal(get().userKey, arr);
    void serverSaveProject(proj);
    // 빈 양식은 autocalc=false (바닐라 openProject(id,false)) —
    // 자동 재계산 없이 열고, 채팅 인트로도 빈 양식 안내 문구로 분기시킨다.
    set({
      projects: arr,
      busy: null,
      current: { project: proj, payload, result: null },
      versions: [],
      fileLinks: [],
      blankIntroId: proj.id,
    });
    void get().loadVersions();
    return proj.id;
  },

  createFromExcel: async (files, name, date, fye) => {
    if (!files.length) {
      alert("업로드할 파일(엑셀, PDF, Word, PowerPoint, 이미지 등)을 먼저 선택하세요.");
      return null;
    }
    set({ busy: "문서에서 재무정보를 추출 중입니다... (수십 초 소요될 수 있습니다)" });
    try {
      // 1) intake — multipart (api 래퍼는 FormData 면 Content-Type 을 설정하지 않는다)
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      fd.append("target_name", name);
      fd.append("valuation_date", date);
      fd.append("fiscal_year_end_month", fye);
      fd.append("forecast_full_years", "5");
      const d = await api<OkResp & { session_id?: string; missing_required?: string[] }>(
        "/agent/dcf/intake",
        { method: "POST", body: fd },
      );
      if (!d.ok || !d.session_id) throw new Error(d.message || "추출 실패");
      const missing = d.missing_required || [];
      // 2) finalize — 추출 결과 → 평가 payload
      set({ busy: "추출 결과로 평가 입력을 구성 중입니다..." });
      const f = await api<OkResp & { payload?: DcfPayload }>(
        `/agent/dcf/intake/${encodeURIComponent(d.session_id)}/finalize`,
        { method: "POST", body: JSON.stringify({}) },
      );
      if (!f.ok || !f.payload) throw new Error(f.message || "구성 실패");
      const payload = f.payload;
      payload.target_name = name;
      payload.valuation_date = date;
      payload.fiscal_year_end_month = parseInt(fye, 10) || 12;
      const proj: DcfProject = {
        id: "p" + Date.now(),
        name,
        date,
        payload,
        pps: null,
        updatedAt: todayStr(),
      };
      const arr = [proj, ...get().projects];
      saveLocal(get().userKey, arr);
      void serverSaveProject(proj);
      set({ projects: arr, busy: null });
      get().openProject(proj.id);
      return { id: proj.id, missing };
    } catch (e) {
      set({ busy: null });
      if (!handleAuthError(e)) alert("파일 업로드 처리 실패: " + errMsg(e));
      return null;
    }
  },

  openProject: (id) => {
    const proj =
      get().projects.find((p) => p.id === id) ||
      loadLocal(get().userKey).find((p) => p.id === id);
    if (!proj) {
      alert("프로젝트를 찾을 수 없습니다.");
      return false;
    }
    set({
      current: { project: proj, payload: proj.payload, result: proj.result || null },
      versions: [],
      fileLinks: [],
      blankIntroId: null,
    });
    void get().loadVersions();
    // 저장된 결과가 있으면 즉시 표시(엔진 재실행 없음), 없으면 자동 계산 — 바닐라 openProject
    if (!proj.result) void get().recalc();
    return true;
  },

  closeProject: () =>
    set({
      current: null,
      versions: [],
      fileLinks: [],
      blankIntroId: null,
      lastPatchPaths: [],
      patchDotTabs: [],
    }),

  saveProject: () => {
    const cur = get().current;
    if (!cur) return;
    const b = cur.result?.bridge;
    const pps = b
      ? ((b.diluted_price_per_share || b.price_per_share) ?? null)
      : (cur.project.pps ?? null);
    const rec: DcfProject = {
      ...cur.project,
      name: cur.payload.target_name || cur.project.name,
      date: cur.payload.valuation_date || cur.project.date,
      payload: cur.payload,
      pps,
      result: cur.result || cur.project.result || null,
      updatedAt: todayStr(),
    };
    const arr = [...get().projects];
    const i = arr.findIndex((p) => p.id === rec.id);
    if (i >= 0) arr[i] = rec;
    else arr.unshift(rec);
    saveLocal(get().userKey, arr);
    void serverSaveProject(rec);
    set({ projects: arr, current: { ...cur, project: rec } });
  },

  deleteProject: (id) => {
    const arr = get().projects.filter((p) => p.id !== id);
    saveLocal(get().userKey, arr);
    void serverDeleteProject(id);
    try {
      // 바닐라 delProject 와 동일 — 프로젝트별 채팅 캐시도 정리
      localStorage.removeItem(`va_dcf_chat::${get().userKey || "_anon"}::${id}`);
    } catch {
      /* noop */
    }
    const cur = get().current;
    if (cur && cur.project.id === id) set({ projects: arr, current: null });
    else set({ projects: arr });
  },

  recalc: async () => {
    const cur = get().current;
    if (!cur) return false;
    const missing = validateRequired(cur.payload);
    if (missing.length) {
      alert("계산을 시작하기 전에 다음 값을 입력해주세요:\n\n· " + missing.join(", "));
      return false;
    }
    set({ busy: "DeepSearch 밸류에이션 엔진이 계산 중입니다..." });
    try {
      const d = await api<OkResp & { result?: DcfResult }>("/agent/dcf/calculate", {
        method: "POST",
        body: JSON.stringify({ payload: cur.payload }),
      });
      if (!d.ok) throw new Error(d.message || "백엔드 오류");
      const now = get().current;
      if (!now || now.project.id !== cur.project.id) return false; // 그 사이 프로젝트 전환
      set({ current: { ...now, result: d.result ?? null } });
      get().saveProject();
      return true;
    } catch (e) {
      if (!handleAuthError(e)) alert("계산 실패: " + errMsg(e));
      return false;
    } finally {
      set({ busy: null });
    }
  },

  setPayloadField: (path, value) => {
    const cur = get().current;
    if (!cur) return;
    const segs = path.split(".");
    const payload: DcfPayload = { ...cur.payload };
    let obj: Record<string, unknown> = payload;
    for (let i = 0; i < segs.length - 1; i++) {
      const next = obj[segs[i]];
      const clone: Record<string, unknown> =
        next && typeof next === "object" && !Array.isArray(next)
          ? { ...(next as Record<string, unknown>) }
          : {};
      obj[segs[i]] = clone;
      obj = clone;
    }
    const last = segs[segs.length - 1];
    if (value === undefined) delete obj[last];
    else obj[last] = value;
    set({ current: { ...cur, payload } });
  },

  setDiscountRateDirect: (rate) => {
    const cur = get().current;
    if (!cur) return;
    // 바닐라 syncWaccDiscountToPayload(useWaccModule=false 분기)와 동일 —
    // WACC 모듈 키를 지워야 백엔드가 discount_rate 를 그대로 사용한다.
    const payload: DcfPayload = { ...cur.payload };
    delete payload.peer_symbols;
    delete payload.peer_input_method;
    delete payload.business_text;
    delete payload.wacc_input;
    payload.discount_rate = rate;
    set({ current: { ...cur, payload } });
  },

  applyWaccModule: (body) => {
    const cur = get().current;
    if (!cur) return;
    const payload: DcfPayload = { ...cur.payload };
    delete payload.discount_rate;
    payload.peer_input_method = body.peer_input_method;
    if (body.peer_input_method === "direct") {
      payload.peer_symbols = body.peer_symbols || [];
      delete payload.business_text;
    } else {
      payload.business_text = body.business_text;
      delete payload.peer_symbols;
    }
    const wi: Record<string, unknown> = {
      mrp: body.mrp,
      size_premium_bucket: body.size_premium_bucket,
      tax_rate: body.tax_rate,
      beta_frequency: body.beta_frequency,
      beta_window_years: body.beta_window_years,
      aggregation: body.aggregation,
      rf_tenor: body.rf_tenor,
      kd_credit_rating: body.kd_credit_rating,
      kd_tenor: body.kd_tenor,
    };
    if (body.rf_override != null) wi.rf_override = body.rf_override;
    if (body.kd_override != null) wi.kd_override = body.kd_override;
    if (body.size_premium_override != null)
      wi.size_premium_override = body.size_premium_override;
    if (body.peer_top_k != null) wi.peer_top_k = body.peer_top_k;
    payload.wacc_input = wi;
    set({ current: { ...cur, payload } });
  },

  setBusy: (msg) => set({ busy: msg }),

  applyPatch: (patch) => {
    const cur = get().current;
    if (!cur || !patch || typeof patch !== "object") return;
    const payload = deepMerge(cur.payload, patch) as DcfPayload;
    // 시각 피드백 (바닐라 applyDcfPatch 의 highlight) —
    // 변경 경로는 입력 셀 cell-flash, 변경 탭은 탭 버튼 파란 dot 로 표시된다.
    const paths = flattenPatchPaths(patch);
    const seq = get().patchSeq + 1;
    const dots = new Set(get().patchDotTabs);
    patchTabsOf(paths).forEach((tb) => dots.add(tb));
    set({
      current: { ...cur, payload },
      lastPatchPaths: paths,
      patchSeq: seq,
      patchDotTabs: [...dots],
    });
    get().saveProject(); // 바닐라 applyDcfPatch 말미의 saveProject(true)
    // flash 는 1.5초 뒤 자동 해제 (바닐라 cell-flash setTimeout 1600ms) —
    // 그 사이 새 patch 가 오면(seq 증가) 이 타이머는 무시된다.
    setTimeout(() => {
      if (get().patchSeq === seq) set({ lastPatchPaths: [] });
    }, 1500);
  },

  clearPatchDot: (tab) => {
    if (get().patchDotTabs.includes(tab))
      set({ patchDotTabs: get().patchDotTabs.filter((tb) => tb !== tab) });
  },

  setFileLinks: (links) => set({ fileLinks: links }),

  addFileLink: (link) => set({ fileLinks: [link, ...get().fileLinks] }),

  loadVersions: async () => {
    const cur = get().current;
    if (!cur) {
      set({ versions: [] });
      return;
    }
    const pid = cur.project.id;
    const items = await serverListVersions(pid);
    // 그 사이 다른 프로젝트로 전환됐으면 무시
    if (get().current?.project.id !== pid) return;
    set({ versions: items || [] });
  },

  saveWithVersion: async () => {
    const cur = get().current;
    if (!cur) return;
    get().saveProject(); // 프로젝트 저장 (캐시+서버)
    const b = get().current?.result?.bridge;
    const now = new Date();
    const ver: DcfVersion = {
      id: "v" + Date.now(),
      label: autoVersionLabel(now),
      payload: JSON.parse(JSON.stringify(cur.payload)) as DcfPayload,
      pps: b
        ? ((b.diluted_price_per_share || b.price_per_share) ?? null)
        : (cur.project.pps ?? null),
      wacc: cur.payload.discount_rate ?? null,
      g: cur.payload.terminal_growth_rate ?? null,
      createdAt: now.toISOString().slice(0, 16).replace("T", " "),
    };
    await serverSaveVersion(cur.project.id, ver);
    await get().loadVersions(); // 버전 카운트 갱신
  },

  restoreVersion: async (vid) => {
    const cur = get().current;
    if (!cur) return;
    const v = get().versions.find((x) => x.id === vid);
    if (!v || !v.payload) {
      alert("버전을 찾을 수 없습니다.");
      return;
    }
    const payload = JSON.parse(JSON.stringify(v.payload)) as DcfPayload;
    set({ current: { ...cur, payload } });
    get().saveProject(); // 캐시+서버 프로젝트 갱신
    await get().recalc(); // 복원된 값으로 재계산
  },

  deleteVersion: (vid) => {
    const cur = get().current;
    set({ versions: get().versions.filter((x) => x.id !== vid) });
    if (cur) void serverDeleteVersion(cur.project.id, vid);
  },
}));
